import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core';
import { NgClass } from '@angular/common';
import { TooltipDirective } from '../directives/tooltip.directive.js';
import { ViewportService } from '../services/viewport.service.js';

export interface ZoomBarSegment {
  key: string;
  leftPct: number;
  widthPct: number;
  cls: string;   // literal Tailwind classes (background + text)
  label: string;
  tip: string;
}

export interface ZoomBarEdgeDrag {
  edge: 'start' | 'end';
  start: number;
  end: number;
}

// Heatmap zoom bar — a Premiere-style scrollbar under the heatmap. The track is the whole
// stream (Twitch categories painted on it), the thumb is the visible window: drag the
// thumb = pan, drag a thumb edge = zoom with the other edge fixed, press the track = centre
// the window there and keep dragging, double-click = whole stream ⇄ detail. A red tick on
// the track marks the playhead (the heatmap may be zoomed far away from it).
// Emits times in seconds; AppComponent turns them into step + scrollLeft (utils/heatmap-zoom).
// Pointer capture on the track with move/up bound on it — no window listeners (they'd run
// app-wide change detection on every mouse move). Edge drags are relative to the press
// (no jump when the handle is grabbed off-centre) and keep the fixed edge from the press,
// so scrollLeft rounding can't creep it.
// Looks like Premiere's: a rounded thumb with a ring at each end (the zoom grips). The thumb
// is never narrower than THUMB_MIN_PX (two rings + room to grab the middle) — then it's
// kept inside the track instead of sticking out past its right end.
@Component({
  selector: 'app-heatmap-zoom-bar',
  standalone: true,
  imports: [NgClass, TooltipDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
  template: `
    <div #track class="relative h-full select-none touch-none"
         [ngClass]="dragMode === 'pan' ? 'cursor-grabbing' : dragMode ? 'cursor-ew-resize' : 'cursor-pointer'"
         role="scrollbar" aria-orientation="horizontal" aria-controls="heatmap-container"
         aria-valuemin="0" aria-valuemax="100" [attr.aria-valuenow]="ariaValue"
         (pointerdown)="onPointerDown($event)"
         (pointermove)="onPointerMove($event)"
         (pointerup)="onPointerUp($event)"
         (pointercancel)="onPointerUp($event)"
         (dblclick)="onDoubleClick($event)">
      <div class="absolute inset-0 rounded-full bg-gray-800/80 overflow-hidden">
        @for (s of segments; track s.key) {
          <div class="absolute inset-y-0 flex items-center overflow-hidden border-r border-gray-950/70"
               [ngClass]="s.cls"
               [style.left.%]="s.leftPct"
               [style.width.%]="s.widthPct"
               [appTooltip]="s.tip">
            <span class="px-2 text-[9px] leading-none font-medium whitespace-nowrap truncate">{{ s.label }}</span>
          </div>
        }
      </div>
      @if (playheadPct !== null) {
        <div class="absolute inset-y-0 w-0.5 -ml-px bg-red-500 pointer-events-none" [style.left.%]="playheadPct"></div>
      }
      <div data-zb="pan" class="absolute inset-y-0 rounded-full bg-white/15 ring-1 ring-inset ring-white/75 hover:bg-white/25 transition-colors"
           [ngClass]="dragMode ? '' : 'cursor-grab'"
           [style.left]="thumbLeftCss"
           [style.width]="thumbWidthCss">
        <div data-zb="start" class="absolute inset-y-0 left-0 flex items-center justify-center cursor-ew-resize group"
             [ngClass]="viewport.prefersCoarsePointer ? 'w-6' : 'w-3.5'">
          <div class="w-2.5 h-2.5 rounded-full border-2 border-gray-300 bg-gray-900 group-hover:border-white pointer-events-none"></div>
        </div>
        <div data-zb="end" class="absolute inset-y-0 right-0 flex items-center justify-center cursor-ew-resize group"
             [ngClass]="viewport.prefersCoarsePointer ? 'w-6' : 'w-3.5'">
          <div class="w-2.5 h-2.5 rounded-full border-2 border-gray-300 bg-gray-900 group-hover:border-white pointer-events-none"></div>
        </div>
      </div>
    </div>
  `,
})
export class HeatmapZoomBarComponent {
  @Input() endSec = 0;
  @Input() viewStart = 0;
  @Input() viewEnd = 0;
  @Input() segments: ZoomBarSegment[] = [];
  /** Playhead, % of the track (rounded by the parent so this isn't re-checked every frame). */
  @Input() playheadPct: number | null = null;

  /** New window start (seconds) — pan, step unchanged. */
  @Output() panTo = new EventEmitter<number>();
  @Output() edgeDrag = new EventEmitter<ZoomBarEdgeDrag>();
  /** Double-click at this time: whole stream ⇄ detail around it. */
  @Output() toggleFit = new EventEmitter<number>();

  private static readonly THUMB_MIN_PX = 36;
  @ViewChild('track', { static: true }) private trackRef!: ElementRef<HTMLElement>;
  viewport = inject(ViewportService);

  private drag: { mode: 'pan' | 'start' | 'end'; pointerId: number; x0: number; start0: number; end0: number } | null = null;
  dragMode: 'pan' | 'start' | 'end' | null = null;

  get thumbLeft(): number {
    return this.endSec > 0 ? this.viewStart / this.endSec * 100 : 0;
  }

  get thumbWidth(): number {
    return this.endSec > 0 ? (this.viewEnd - this.viewStart) / this.endSec * 100 : 100;
  }

  // max(): never narrower than the rings need; min(): a widened thumb stays inside the track.
  get thumbWidthCss(): string {
    return `max(${HeatmapZoomBarComponent.THUMB_MIN_PX}px, ${this.thumbWidth}%)`;
  }

  get thumbLeftCss(): string {
    return `min(${this.thumbLeft}%, calc(100% - ${this.thumbWidthCss}))`;
  }

  get ariaValue(): number {
    return Math.round(this.thumbLeft);
  }

  private secPerPx(): number {
    const w = this.trackRef.nativeElement.getBoundingClientRect().width;
    return w > 0 ? this.endSec / w : 0;
  }

  private secAt(clientX: number): number {
    const r = this.trackRef.nativeElement.getBoundingClientRect();
    if (!(r.width > 0)) return 0;
    return Math.min(this.endSec, Math.max(0, (clientX - r.left) / r.width * this.endSec));
  }

  onPointerDown(event: PointerEvent): void {
    if (this.drag || !(this.endSec > 0) || (event.pointerType === 'mouse' && event.button !== 0)) return;
    const hit = (event.target as HTMLElement | null)?.closest('[data-zb]') as HTMLElement | null;
    const zone = hit?.dataset['zb'] as 'pan' | 'start' | 'end' | undefined;
    let start0 = this.viewStart;
    let end0 = this.viewEnd;
    if (!zone) {
      // Press on the track: centre the window on it, then drag it from there.
      const span = end0 - start0;
      start0 = Math.min(Math.max(0, this.secAt(event.clientX) - span / 2), Math.max(0, this.endSec - span));
      end0 = start0 + span;
      this.panTo.emit(start0);
    }
    this.trackRef.nativeElement.setPointerCapture?.(event.pointerId);
    this.drag = { mode: zone ?? 'pan', pointerId: event.pointerId, x0: event.clientX, start0, end0 };
    this.dragMode = this.drag.mode;
  }

  onPointerMove(event: PointerEvent): void {
    const d = this.drag;
    if (!d || event.pointerId !== d.pointerId) return;
    const dt = (event.clientX - d.x0) * this.secPerPx();
    if (d.mode === 'pan') {
      this.panTo.emit(d.start0 + dt);
    } else if (d.mode === 'start') {
      this.edgeDrag.emit({ edge: 'start', start: Math.min(this.endSec, Math.max(0, d.start0 + dt)), end: d.end0 });
    } else {
      this.edgeDrag.emit({ edge: 'end', start: d.start0, end: Math.min(this.endSec, Math.max(0, d.end0 + dt)) });
    }
  }

  onPointerUp(event: PointerEvent): void {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;
    this.drag = null;
    this.dragMode = null;
  }

  onDoubleClick(event: MouseEvent): void {
    if (this.endSec > 0) this.toggleFit.emit(this.secAt(event.clientX));
  }
}
